/**
 * Libraries
 */

import { defineStore } from 'pinia';
import { setUser as setSentryUser } from '@sentry/vue';

/**
 * Config
 */

import { setIntercomSettings } from '@config/intercom';

/**
 * Composables
 */

import {
  adminRoles,
  legacyRoles,
  roles,
  userRoles,
} from '@shared/composables/auth/userRoles';

/**
 * Types
 */

import type { KeycloakTokenParsed } from 'keycloak-js';
import type {
  KeycloakStore,
  KeycloakResource,
  KeycloakRole,
  KeycloakResourcesRoles,
} from '@core/types/Keycloak';

const {
  INITIATOR,
  FIRST_VALIDATOR,
  SECOND_VALIDATOR,
  ADMIN_DQC,
  ADMIN_DQC_EDITOR,
  ADMIN_DQC_MANAGER,
} = roles;

function isKeycloakRoles(roles: unknown): roles is KeycloakRole[] {
  return (
    Array.isArray(roles) && roles.every((role) => typeof role === 'string')
  );
}

function hasResourceRoles(
  state: KeycloakStore,
  resource: KeycloakResource,
  roles: KeycloakRole[],
) {
  return roles.some((role) =>
    (state.resourcesRoles[resource] || []).includes(role),
  );
}

function hasAdminRoles(state: KeycloakStore) {
  return hasResourceRoles(state, 'lea', [...adminRoles, ...legacyRoles]);
}

function hasUserRoles(state: KeycloakStore) {
  return hasResourceRoles(state, 'lea', [...userRoles]);
}

const useKeycloakStore = defineStore('keycloak', {
  state: (): KeycloakStore => ({
    isAuthenticated: false,
    token: '',
    roles: [],
    user: {
      id: '',
      preferred_username: '',
      given_name: '',
      family_name: '',
      email: '',
      full_name: '',
      roles: [],
    },
    resourcesRoles: {
      'realm-management': [],
      lea: [],
      account: [],
    },
  }),
  getters: {
    getRoles: (state) => () => state.roles,
    getUserRoles: (state) => (resource: KeycloakResource) =>
      isKeycloakRoles(state.resourcesRoles[resource]) &&
      state.resourcesRoles[resource],
    isAdmin: (state) => hasAdminRoles(state),
    isInitiator: (state) => hasResourceRoles(state, 'lea', [INITIATOR]),
    isFirstValidator: (state) =>
      hasResourceRoles(state, 'lea', [FIRST_VALIDATOR]),
    isSecondValidator: (state) =>
      hasResourceRoles(state, 'lea', [SECOND_VALIDATOR]),
    isValidator: (state) =>
      hasResourceRoles(state, 'lea', [FIRST_VALIDATOR, SECOND_VALIDATOR]),
    isDqc: (state) =>
      hasResourceRoles(state, 'lea', [
        ADMIN_DQC,
        ADMIN_DQC_EDITOR,
        ADMIN_DQC_MANAGER,
      ]),
    isOnlyAdmin: (state) => hasAdminRoles(state) && !hasUserRoles(state),
    isTicketUser: (state) => hasUserRoles(state),
    isUbaqUser: (state) => state.user.email.includes('@ubaq.io'),
  },
  actions: {
    setKeycloakData(parsedToken: KeycloakTokenParsed) {
      this.setRoles(parsedToken);
      this.setUser(parsedToken);
      this.setRessourcesRoles(parsedToken);

      setSentryUser({
        id: this.user.id,
        username: this.user.preferred_username,
        email: this.user.email,
        full_name: this.user.full_name,
        roles: this.user.roles.join(', '),
      });
      setIntercomSettings({
        email: this.user.email,
        full_name: this.user.full_name,
      });
    },
    setIsAuthenticated(value: boolean) {
      this.isAuthenticated = value;
    },
    setToken(value: string) {
      this.token = value;
    },
    setRoles(parsedToken: KeycloakTokenParsed) {
      const roles =
        (isKeycloakRoles(parsedToken.resource_access?.lea?.roles) &&
          parsedToken.resource_access?.lea?.roles) ||
        [];
      this.roles = roles;
    },
    setUser(parsedToken: KeycloakTokenParsed) {
      this.user = {
        ...parsedToken.user,
        roles: parsedToken.resource_access?.lea?.roles || [],
        full_name: `${parsedToken.user?.given_name} ${parsedToken.user?.family_name}`,
      };
    },
    setRessourcesRoles(parsedToken: KeycloakTokenParsed) {
      if (parsedToken.resource_access) {
        const resourcesRoles = Object.fromEntries(
          Object.entries(parsedToken.resource_access).map(([key, value]) => [
            key,
            value.roles,
          ]),
        ) as unknown as KeycloakResourcesRoles;

        this.resourcesRoles = resourcesRoles;
      }
    },
    clearStore() {
      this.isAuthenticated = false;
      this.token = '';
      this.roles = [];
      this.user = {
        id: '',
        preferred_username: '',
        given_name: '',
        family_name: '',
        email: '',
        full_name: '',
        roles: [],
      };
      this.resourcesRoles = {
        'realm-management': [],
        lea: [],
        account: [],
      };
      setSentryUser(null);
    },
  },
  persist: true,
});

export { useKeycloakStore };
